<script setup lang="ts">
// Fixed left sidebar of the desktop shell (228px): brand, progress card, exam-goal card, nav groups,
// member promo card, help link, domain and the collapse button. Markup and class names follow production
// (index.html #sidebar, assets/js/app.js renderNav) because the generated legacy CSS styles exactly that.
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '../components/AppIcon.vue'
import { LAB_NAV, MAIN_NAV, isActive } from './nav'
import { learning } from '../stores/learning'
import { openContact } from '../stores/overlays'
import { daysToExam, examPrefs, setExamDate } from '../stores/examPrefs'

const route = useRoute()
const router = useRouter()

let storedExpanded = true
try { storedExpanded = localStorage.getItem('jx_sidebar_expanded') !== '0' } catch { /* default expanded */ }
const expanded = ref(storedExpanded)
function toggle() {
  expanded.value = !expanded.value
  try { localStorage.setItem('jx_sidebar_expanded', expanded.value ? '1' : '0') } catch { /* ignore */ }
}

const goalTitle = computed(() => {
  if (!examPrefs.customDate) return '设置备考目标'
  const days = daysToExam(examPrefs.customDate)
  return days === 0 ? '考试日到了' : `距考试 ${days ?? '--'} 天`
})
const goalLabel = computed(() => examPrefs.customDate
  ? `${examPrefs.customDate.slice(0, 4)}.${examPrefs.customDate.slice(5).replace('-', '.')} · 自定义`
  : '由你决定学习节奏')
const minDate = new Date().toISOString().slice(0, 10)
</script>

<template>
  <aside class="sidebar" :class="{ expanded }" id="sidebar">
    <div class="sb-brand" title="导游学霸 · 全国备考" @click="router.push('/')">
      <span class="sb-brand-mark"><img src="/assets/desktop-ui/logo.png" alt="" width="40" height="40"></span>
      <span class="sb-brand-name"><b>导游学霸</b><small>GUIDE · LEARN · PASS</small></span>
    </div>
    <div class="sb-progress" id="sbProgress" title="查看我的备考进度" role="button" tabindex="0"
      @click="router.push('/me')" @keydown.enter.prevent="router.push('/me')" @keydown.space.prevent="router.push('/me')">
      <div class="sb-progress-status">
        <span class="sb-progress-state"><i></i><span>备考中</span></span>
        <span class="sb-progress-streak" id="sbStreak" title="连续学习天数">连续 <b id="sbStreakNum">{{ learning.streak }}</b> 天</span>
      </div>
      <div class="sb-progress-num"><b id="sbProgressNum">{{ learning.stablePercent }}%</b><span>稳定掌握</span></div>
      <div class="sb-progress-bar"><i id="sbProgressFill" :style="{ width: learning.stablePercent + '%' }"></i></div>
      <div class="sb-progress-label">2026 导游资格考试</div>
    </div>
    <label class="sb-exam-countdown" title="设置个人目标日期，正式考试安排以官方通知为准">
      <span><b id="sbExamCountdown">{{ goalTitle }}</b><small id="sbExamDateLabel">{{ goalLabel }}</small></span>
      <input id="sbExamDateInput" type="date" aria-label="设置备考目标日期" :min="minDate" :value="examPrefs.date"
        @change="setExamDate(($event.target as HTMLInputElement).value)">
    </label>
    <nav class="sb-nav" id="nav">
      <div class="sb-nav-group">备考主线</div>
      <button v-for="item in MAIN_NAV" :key="item.id" type="button" class="sb-nav-item" :class="{ on: isActive(item, route.path) }"
        :title="item.label" :aria-current="isActive(item, route.path) ? 'page' : undefined" @click="router.push(item.to)">
        <span class="sb-nav-ic"><AppIcon :name="item.icon" variant="bare" /></span>
        <span class="sb-nav-name">{{ item.label }}</span>
      </button>
      <div class="sb-nav-group">体验与共创</div>
      <button v-for="item in LAB_NAV" :key="item.id" type="button" class="sb-nav-item is-experimental" :class="{ on: isActive(item, route.path) }"
        :title="`${item.label} · 开放体验`" @click="router.push(item.to)">
        <span class="sb-nav-ic"><AppIcon :name="item.icon" variant="bare" /></span>
        <span class="sb-nav-name">{{ item.label }}</span>
        <span class="sb-nav-status">{{ item.status }}</span>
      </button>
    </nav>
    <div class="sb-footer">
      <div class="dh-sidebar-footer">
        <button type="button" class="dh-member-card" @click="router.push('/me')"><b>把图鉴，收入你的知识库</b><span>新用户注册登录一次性3次解锁，已解锁保留</span><small>选中的图永久保留</small></button>
        <button type="button" class="dh-sidebar-help" @click="openContact()">帮助与讨论群 <i>↗</i></button>
        <a class="dh-sidebar-domain" href="https://daoyouxueba.com/">daoyouxueba.com</a>
      </div>
      <button class="sb-toggle" id="sbToggle" :title="expanded ? '收起导航' : '展开导航'" :aria-label="expanded ? '收起导航' : '展开导航'"
        :aria-expanded="expanded" @click="toggle"><span aria-hidden="true">‹</span></button>
    </div>
  </aside>
</template>
